import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { adminApi, formatApiError } from './adminApi';
import { Lock, Save, AlertCircle, CheckCircle2, Mail, Shield, ShieldCheck, ShieldOff, Monitor, X } from 'lucide-react';

const AdminSettings = () => {
  const { user, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  // 2FA management state
  const [twofaInfo, setTwofaInfo] = useState(null);
  const [twofaOpen, setTwofaOpen] = useState(false);
  const [twofaPwd, setTwofaPwd] = useState('');
  const [twofaCode, setTwofaCode] = useState('');
  const [twofaError, setTwofaError] = useState('');
  const [twofaSuccess, setTwofaSuccess] = useState('');
  const [twofaBusy, setTwofaBusy] = useState(false);

  const loadTwofa = () => adminApi.twofaStatus().then(setTwofaInfo).catch(() => setTwofaInfo(null));
  useEffect(() => { loadTwofa(); }, []);

  // Trusted devices state
  const [devices, setDevices] = useState([]);
  const [devicesErr, setDevicesErr] = useState('');
  const loadDevices = () =>
    adminApi
      .listTrustedDevices()
      .then((rows) => setDevices(Array.isArray(rows) ? rows : []))
      .catch(() => setDevices([]));
  useEffect(() => { loadDevices(); }, []);

  const handleRevokeDevice = async (deviceId) => {
    if (!window.confirm('Revoke this trusted device? You’ll be asked for a 2FA code the next time you sign in from it.')) return;
    setDevicesErr('');
    try {
      await adminApi.revokeTrustedDevice(deviceId);
      loadDevices();
    } catch (e) {
      setDevicesErr(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const handleRevokeAllDevices = async () => {
    if (!window.confirm('Sign out of every trusted browser? Everyone (including you) will have to complete 2FA on their next sign-in.')) return;
    setDevicesErr('');
    try {
      await adminApi.revokeAllTrustedDevices();
      loadDevices();
    } catch (e) {
      setDevicesErr(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (next.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (next !== confirm) {
      setError('New passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      await adminApi.changePassword(current, next);
      setSuccess('Password changed. Please sign in again.');
      setCurrent('');
      setNext('');
      setConfirm('');
      setTimeout(() => logout(), 1500);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDisable2FA = async (e) => {
    e.preventDefault();
    setTwofaError('');
    setTwofaSuccess('');
    setTwofaBusy(true);
    try {
      await adminApi.twofaDisable(twofaPwd, twofaCode);
      setTwofaSuccess('2FA disabled. You will be asked to enrol again on your next sign-in.');
      setTwofaPwd('');
      setTwofaCode('');
      setTwofaOpen(false);
      loadTwofa();
    } catch (err) {
      setTwofaError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setTwofaBusy(false);
    }
  };

  return (
    <div className="px-8 lg:px-12 py-10 max-w-3xl" data-testid="admin-settings-page">
      <header className="mb-8">
        <p className="text-[#83BF42] text-xs uppercase tracking-[0.2em] font-['Inter'] mb-2">Account</p>
        <h1 className="text-white text-4xl font-light font-['Inter']">Settings</h1>
        <p className="text-white/40 text-sm font-['Lato'] mt-2">
          Account info and password management.
        </p>
      </header>

      {/* Account card */}
      <div className="bg-[#15151A] border border-white/5 rounded-2xl p-6 mb-6" data-testid="account-card">
        <h2 className="text-white text-base font-medium font-['Inter'] mb-5 flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#83BF42]" /> Signed in as
        </h2>
        <ul className="text-sm text-white/70 font-['Lato'] space-y-2">
          <li className="flex items-center gap-2">
            <Mail className="w-3.5 h-3.5 text-white/40" /> {user?.email}
          </li>
          <li className="flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-white/40" /> {user?.role}
          </li>
        </ul>
      </div>

      {/* Change password */}
      <div className="bg-[#15151A] border border-white/5 rounded-2xl p-6">
        <h2 className="text-white text-base font-medium font-['Inter'] mb-1 flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#83BF42]" /> Change password
        </h2>
        <p className="text-white/40 text-xs font-['Lato'] mb-5">
          You'll be signed out after a successful change.
        </p>

        {error && (
          <div data-testid="settings-error" className="flex items-start gap-2 mb-4 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-xs">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {error}
          </div>
        )}
        {success && (
          <div data-testid="settings-success" className="flex items-start gap-2 mb-4 px-3 py-2.5 rounded-lg bg-[#83BF42]/10 border border-[#83BF42]/20 text-[#83BF42] text-xs">
            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-password-form">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-white/50 mb-1.5">
              Current password
            </label>
            <input
              type="password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="settings-current-password"
              className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-white/50 mb-1.5">
              New password
            </label>
            <input
              type="password"
              required
              minLength={8}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="settings-new-password"
              className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
            />
            <p className="text-white/30 text-xs font-['Lato'] mt-1">
              Minimum 8 characters.
            </p>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-white/50 mb-1.5">
              Confirm new password
            </label>
            <input
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="settings-confirm-password"
              className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            data-testid="settings-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#83BF42] text-[#0F0F11] text-sm font-medium font-['Inter'] hover:bg-[#A8D96C] transition disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {saving ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </div>

      {/* Two-factor auth */}
      <div className="bg-[#15151A] border border-white/5 rounded-2xl p-6 mt-6" data-testid="twofa-card">
        <h2 className="text-white text-base font-medium font-['Inter'] mb-1 flex items-center gap-2">
          {twofaInfo?.enabled ? (
            <ShieldCheck className="w-4 h-4 text-[#83BF42]" />
          ) : (
            <ShieldOff className="w-4 h-4 text-amber-400" />
          )}
          Two-factor authentication
        </h2>
        <p className="text-white/40 text-xs font-['Lato'] mb-5">
          Works with Google Authenticator, Authy, 1Password, Microsoft Authenticator and any other RFC 6238 app.
        </p>

        {twofaSuccess && (
          <div data-testid="twofa-success" className="flex items-start gap-2 mb-4 px-3 py-2.5 rounded-lg bg-[#83BF42]/10 border border-[#83BF42]/20 text-[#83BF42] text-xs">
            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {twofaSuccess}
          </div>
        )}

        {twofaInfo?.enabled ? (
          <>
            <div className="flex items-center justify-between mb-4 p-3 rounded-lg bg-[#83BF42]/5 border border-[#83BF42]/20">
              <div>
                <p className="text-[#83BF42] text-sm font-medium font-['Inter']" data-testid="twofa-status-label">
                  2FA is on
                </p>
                <p className="text-white/50 text-xs font-['Lato'] mt-0.5" data-testid="twofa-backup-remaining">
                  {twofaInfo.backup_codes_remaining} of 8 backup codes remaining
                </p>
              </div>
              {!twofaOpen && (
                <button
                  type="button"
                  onClick={() => setTwofaOpen(true)}
                  data-testid="twofa-disable-open"
                  className="px-3 py-1.5 rounded-full text-xs font-['Inter'] text-red-300 hover:text-red-200 border border-red-500/30 hover:bg-red-500/10 transition-colors"
                >
                  Turn off 2FA
                </button>
              )}
            </div>

            {twofaOpen && (
              <form onSubmit={handleDisable2FA} className="space-y-4 pt-2" data-testid="twofa-disable-form">
                {twofaError && (
                  <div data-testid="twofa-error" className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-xs">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {twofaError}
                  </div>
                )}
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-white/50 mb-1.5">
                    Current password
                  </label>
                  <input
                    type="password"
                    required
                    value={twofaPwd}
                    onChange={(e) => setTwofaPwd(e.target.value)}
                    data-testid="twofa-disable-password"
                    className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-white/50 mb-1.5">
                    Verification code (or backup code)
                  </label>
                  <input
                    type="text"
                    required
                    value={twofaCode}
                    onChange={(e) => setTwofaCode(e.target.value)}
                    data-testid="twofa-disable-code"
                    autoComplete="one-time-code"
                    className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-mono focus:outline-none focus:border-[#83BF42] transition-colors"
                    placeholder="123456 or XXXX-XXXX"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    disabled={twofaBusy}
                    data-testid="twofa-disable-submit"
                    className="px-5 py-2.5 rounded-full text-xs font-medium font-['Inter'] text-red-300 hover:text-red-200 border border-red-500/30 hover:bg-red-500/10 transition disabled:opacity-50"
                  >
                    {twofaBusy ? 'Disabling…' : 'Confirm turn off'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTwofaOpen(false); setTwofaError(''); setTwofaPwd(''); setTwofaCode(''); }}
                    className="px-4 py-2 text-xs text-white/50 hover:text-white/80 font-['Inter']"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </>
        ) : (
          <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-amber-300 text-sm">
            2FA is currently off — you’ll be asked to enrol on your next sign-in.
          </div>
        )}
      </div>

      {/* Trusted devices */}
      <div className="bg-[#15151A] border border-white/5 rounded-2xl p-6 mt-6" data-testid="trusted-devices-card">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-white text-base font-medium font-['Inter'] flex items-center gap-2">
            <Monitor className="w-4 h-4 text-[#83BF42]" /> Trusted browsers
          </h2>
          {devices.length > 0 && (
            <button
              type="button"
              onClick={handleRevokeAllDevices}
              data-testid="trusted-devices-revoke-all"
              className="px-3 py-1 rounded-full text-[11px] text-red-300 hover:text-red-200 border border-red-500/30 hover:bg-red-500/10 transition-colors"
            >
              Sign out other browsers
            </button>
          )}
        </div>
        <p className="text-white/40 text-xs font-['Lato'] mb-5">
          Browsers you’ve ticked “Trust this browser for 30 days” after signing in. Password is still required on every sign-in — the trust cookie only skips the 2FA code step for 30 days.
        </p>

        {devicesErr && (
          <div data-testid="trusted-devices-error" className="flex items-start gap-2 mb-4 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-xs">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {devicesErr}
          </div>
        )}

        {devices.length === 0 ? (
          <div className="text-white/40 text-sm font-['Lato']" data-testid="trusted-devices-empty">
            No trusted browsers yet. Tick the box on your next sign-in to remember this one.
          </div>
        ) : (
          <ul className="divide-y divide-white/5" data-testid="trusted-devices-list">
            {devices.map((d) => (
              <li key={d.id} className="py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm font-['Inter']" data-testid={`trusted-device-label-${d.id}`}>
                      {d.device_label}
                    </span>
                    {d.is_current && (
                      <span
                        data-testid={`trusted-device-current-${d.id}`}
                        className="px-2 py-0.5 rounded-full text-[10px] font-['Inter'] uppercase tracking-wider bg-[#83BF42]/15 text-[#83BF42] border border-[#83BF42]/30"
                      >
                        This browser
                      </span>
                    )}
                  </div>
                  <div className="text-white/40 text-xs font-['Lato'] mt-0.5 truncate">
                    IP {d.ip || '—'} · trusted {d.created_at ? new Date(d.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    {d.last_seen_at ? ` · last seen ${new Date(d.last_seen_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
                    {d.expires_at ? ` · expires ${new Date(d.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRevokeDevice(d.id)}
                  data-testid={`trusted-device-revoke-${d.id}`}
                  className="p-1.5 rounded text-white/40 hover:text-red-400 hover:bg-red-500/10 shrink-0"
                  title="Revoke this browser"
                  aria-label="Revoke"
                >
                  <X className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
